import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { apiFetch, isEmbeddedApp } from '../utils/api';
import './DashboardPage.css';

const quickActions = [
    {
        id: 'create',
        icon: '📸',
        title: 'Create Listing',
        description: 'Take a photo and create a professional listing in seconds',
        path: '/create',
        gradient: 'linear-gradient(135deg, #4F46E5, #7C3AED)'
    },
    {
        id: 'albaran',
        icon: '📄',
        title: 'Scan Delivery Note',
        description: 'Photograph your delivery note to update inventory',
        path: '/albaran',
        gradient: 'linear-gradient(135deg, #10B981, #059669)'
    },
    {
        id: 'inventory',
        icon: '📦',
        title: 'Stock Count',
        description: 'Take a photo of your products to sync stock levels',
        path: '/inventory',
        gradient: 'linear-gradient(135deg, #F59E0B, #D97706)'
    },
    {
        id: 'materials',
        icon: '🧱',
        title: 'Raw Materials',
        description: 'Track your clay, wood, metal, and other supplies',
        path: '/materials',
        gradient: 'linear-gradient(135deg, #EC4899, #BE185D)'
    }
];

const stageTwoActions = [
    {
        id: '3d',
        icon: '🎭',
        title: '3D Models & AR',
        description: 'Generate 3D models for immersive AR shopping',
        path: '/3d',
        gradient: 'linear-gradient(135deg, #06B6D4, #0891B2)',
        badge: 'SOON'
    },
    {
        id: 'ai-agent',
        icon: '🤖',
        title: 'AI Customer Service',
        description: '24/7 AI assistant for customer inquiries',
        path: '/ai-agent',
        gradient: 'linear-gradient(135deg, #8B5CF6, #6366F1)',
        badge: 'SOON'
    },
    {
        id: 'marketplace',
        icon: '🌍',
        title: 'Global Marketplace',
        description: 'Connect with buyers worldwide, enable B2B wholesale',
        path: '/marketplace',
        gradient: 'linear-gradient(135deg, #F97316, #EA580C)',
        badge: 'SOON'
    }
];

interface DashboardStats {
    activeListings: number;
    itemsInStock: number;
    materials: number;
    inventoryValue: string;
}

function DashboardPage() {
    const navigate = useNavigate();
    const [stats, setStats] = useState<DashboardStats>({
        activeListings: 0,
        itemsInStock: 0,
        materials: 0,
        inventoryValue: '0'
    });

    useEffect(() => {
        const loadStats = async () => {
            try {
                if (!isEmbeddedApp() && !localStorage.getItem('artisan_token')) {
                    return;
                }

                const data = await apiFetch('/api/dashboard/stats');
                if (data && data.stats) {
                    setStats(data.stats);
                } else {
                    console.warn('Dashboard stats returned empty or invalid structure', data);
                }
            } catch (error) {
                console.error('Failed to load dashboard stats:', error);
                // Maintain default/zero stats on error instead of crashing or showing broken UI
            }
        };
        loadStats();
    }, []);

    return (
        <div className="dashboard-page animate-fade-in">
            <div className="welcome-section">
                <h1 className="welcome-title">
                    Welcome to <span className="gradient-text">Commerium</span>
                </h1>
                <p className="welcome-subtitle">
                    The complete operating system for artisans
                </p>
            </div>

            <div className="dashboard-actions-grid">
                {quickActions.map((action) => (
                    <div
                        key={action.id}
                        className="action-card"
                        onClick={() => navigate(action.path)}
                        style={{ '--action-gradient': action.gradient } as React.CSSProperties}
                    >
                        <div className="action-icon" style={{ background: action.gradient }}>
                            {action.icon}
                        </div>
                        <div className="action-content">
                            <h3>{action.title}</h3>
                            <p>{action.description}</p>
                        </div>
                        <div className="action-arrow">→</div>
                    </div>
                ))}
            </div>

            <div className="stats-section">
                <h2 className="section-title">Your Workshop Today</h2>
                <div className="stats-grid">
                    <div className="stat-card">
                        <span className="stat-value">{stats.activeListings}</span>
                        <span className="stat-label">Active Shopify Products</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-value">{stats.itemsInStock}</span>
                        <span className="stat-label">Workshop Units</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-value">{stats.materials}</span>
                        <span className="stat-label">Tracked Materials</span>
                    </div>
                    <div className="stat-card">
                        <span className="stat-value">€{stats.inventoryValue}</span>
                        <span className="stat-label">Inventory Value</span>
                    </div>
                </div>
            </div>

            <div className="stage-two-section">
                <h2 className="section-title">
                    <span className="gradient-text">Stage Two</span> Features
                </h2>
                <div className="dashboard-actions-grid">
                    {stageTwoActions.map((action) => (
                        <div
                            key={action.id}
                            className="action-card stage-two-card"
                            onClick={() => navigate(action.path)}
                            style={{ '--action-gradient': action.gradient } as React.CSSProperties}
                        >
                            {'badge' in action && (
                                <span className="new-badge">{action.badge}</span>
                            )}
                            <div className="action-icon" style={{ background: action.gradient }}>
                                {action.icon}
                            </div>
                            <div className="action-content">
                                <h3>{action.title}</h3>
                                <p>{action.description}</p>
                            </div>
                            <div className="action-arrow">→</div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="recent-section">
                <h2 className="section-title">Recent Activity</h2>
                <div className="empty-state card-glass">
                    <span className="empty-icon">🎨</span>
                    <p>Start by creating your first listing or scanning a delivery note</p>
                </div>
            </div>

            <div className="dashboard-footer-meta">
                Artisan OS v1.9
            </div>
        </div>
    );
}

export default DashboardPage;
